import { useMemo, useState } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import type { PinnedOption } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions } from '@gonfalon/dataviz-experimental/types';
import { getContextKindsByProjectKey } from '@gonfalon/openapi/operations/getContextKindsByProjectKey';
import { getSuggestions } from '@gonfalon/openapi/operations/getSuggestions';
import { getContextAttributeNamesQuery } from '@gonfalon/rest-api/queries/context-options';
import { useQueries, useQuery } from '@tanstack/react-query';

import {
  combineContextAttributeNameOptions,
  type ContextAttributeNameOptions,
} from './contextAttributeNamesToFieldOptions';
import { CONTEXT_ATTRS_PREFIX } from './metricEventFilterFields';
import { buildSuggestionsPayload, mapSuggestionsToFieldOptions } from './suggestionFieldOptions';

export const PINNED_KIND_ATTRIBUTE_LIMIT = 20;

export function useEventFieldOptions(projectKey: string): FieldOptions[] {
  const [now] = useState(() => Date.now());
  const sevenDaysAgo = now - 604_800_000;
  const { data } = useQuery({
    queryFn: () => getSuggestions({ body: buildSuggestionsPayload(projectKey, sevenDaysAgo, now) }),
    queryKey: ['suggestions', 'sdk', 'custom_event', projectKey],
    enabled: !!projectKey,
  });

  return useMemo<FieldOptions[]>(() => mapSuggestionsToFieldOptions(data?.data?.items ?? []), [data]);
}

export function useContextKindOptions(projectKey: string): PinnedOption[] {
  const { data } = useQuery({
    queryFn: () => getContextKindsByProjectKey({ projectKey }),
    queryKey: ['contextKinds', projectKey],
    enabled: !!projectKey,
  });
  return useMemo<PinnedOption[]>(
    () =>
      (data?.data?.items ?? []).filter((kind) => !kind.archived).map((kind) => ({ value: kind.key, label: kind.name })),
    [data],
  );
}

export function useContextScopedFieldOptions(
  projectKey: string,
  selectedKinds: string[],
): { options: FieldOptions[]; isPending: boolean } {
  const [now] = useState(() => Date.now());
  const sevenDaysAgo = now - 604_800_000;
  return useQueries({
    queries: selectedKinds.map((kind) => ({
      queryFn: () => getSuggestions({ body: buildSuggestionsPayload(projectKey, sevenDaysAgo, now, kind) }),
      queryKey: ['suggestions', 'sdk', 'custom_event', 'contextKind', projectKey, kind],
      enabled: !!projectKey,
    })),
    combine: (results) => {
      const seen = new Set<string>();
      const merged: FieldOptions[] = [];
      for (const result of results) {
        for (const option of mapSuggestionsToFieldOptions(result.data?.data?.items ?? [])) {
          if (!option.field.startsWith(CONTEXT_ATTRS_PREFIX) || seen.has(option.field)) {
            continue;
          }
          seen.add(option.field);
          merged.push(option);
        }
      }
      return {
        options: merged,
        // isLoading = isPending && isFetching — disabled queries stay isPending without fetching.
        isPending: selectedKinds.length > 0 && results.some((result) => result.isLoading),
      };
    },
  });
}

export function useContextAttributeNameOptions(
  projectKey: string,
  selectedKinds: string[],
): ContextAttributeNameOptions & { isPending: boolean } {
  const environmentKey = useSelectedEnvironmentKey({ optional: true });
  return useQueries({
    queries: selectedKinds.map((kind) => ({
      ...getContextAttributeNamesQuery({
        projectKey,
        environmentKey: environmentKey ?? '',
        query: { filter: `kind equals ${JSON.stringify(kind)}`, limit: PINNED_KIND_ATTRIBUTE_LIMIT },
      }),
      enabled: Boolean(projectKey && environmentKey),
    })),
    combine: (results) => ({
      ...combineContextAttributeNameOptions(results, PINNED_KIND_ATTRIBUTE_LIMIT),
      // isLoading = isPending && isFetching — disabled (no-env) queries stay isPending without fetching.
      isPending: selectedKinds.length > 0 && results.some((result) => result.isLoading),
    }),
  });
}
